import React, { Fragment, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { designSystem } from '@yola/ws-ui';
import { siteLocales } from '@yola/ws-sdk';
import getLanguages from '../../website-settings/helpers/get-languages';
import getCountries from '../../website-settings/helpers/get-countries';
import getLanguageData from '../../website-settings/helpers/get-language-data';
import constants from '../constants';

const { SearchableSelectField, LanguageSelect } = designSystem;

const {
  linkSettings: { FIELD_NAMES },
} = constants;

const findOptionByValue = (value, options) =>
  options.find((option) => option.value === value || option.value.value === value);

const defaultCaptions = {
  noResultsText: '',
};

function PageSelector({
  onChange,
  reference,
  onSelectActionValue,
  options,
  captions = defaultCaptions,
  control,
}) {
  const locales = siteLocales.accessors.getSiteLocales();
  const currentLocale = siteLocales.accessors.getCurrentLocale();

  const localesOptions = useMemo(() => {
    const langMap = getLanguages();
    const countriesMap = getCountries();
    return locales.map((local) => {
      const {
        name,
        country: { iconComponent },
      } = getLanguageData(local, langMap, countriesMap);
      return {
        value: local,
        label: name,
        iconComponent,
      };
    });
  }, [locales]);

  const findActiveLang = () => {
    const activeOption = options.find(({ value }) => value === reference);
    return activeOption ? activeOption.locale : currentLocale;
  };

  const [activeLocale, setActiveLocale] = useState(findActiveLang());

  const handleSwitchLanguage = ({ value: langValue }) => {
    setActiveLocale(langValue);
    const newActiveOption = options.find(({ locale }) => locale === langValue);
    onChange(newActiveOption);
  };

  const pagesOptions =
    localesOptions.length > 1 ? options.filter(({ locale }) => locale === activeLocale) : options;
  const defaultLanguageOption = findOptionByValue(activeLocale, localesOptions);

  return (
    <Fragment>
      {localesOptions.length > 1 && (
        <LanguageSelect
          options={localesOptions}
          defaultValue={defaultLanguageOption}
          onChange={handleSwitchLanguage}
        />
      )}

      <SearchableSelectField
        name={FIELD_NAMES.reference}
        control={control}
        noResultsText={captions.noResultsText}
        options={pagesOptions}
        onChange={onSelectActionValue}
      />
    </Fragment>
  );
}

PageSelector.propTypes = {
  reference: PropTypes.string.isRequired,
  onSelectActionValue: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  captions: PropTypes.shape({
    noResultsText: PropTypes.string,
  }),
  control: PropTypes.shape({}).isRequired,
};

export default PageSelector;
